import { Debug } from '@infiniteobjects/core-library-web'
import * as Sentry from '@sentry/browser'
import kebabCase from 'lodash/fp/kebabCase'
import { SENTRY_DSN, VERSION } from '../constants'
import { getPrintButtonProps } from '../global/io-print-button-config'

const debug = Debug.create('print-button:sentry')

/**
 * In order to be sure we have isolated our sentry instances to only report
 * to our DSN it is recommended to use a Sentry.Hub().  We instantiate the
 * singleton hub above the class definition in this file.  The line below
 * binds all of the BrowserClient instances to the same hub.  It is possible
 * to setup "global" config/context on the Hub
 *
 * See link below for more info:
 * https://docs.sentry.io/platforms/javascript/enriching-events/scopes/#whats-a-scope-whats-a-hub
 */
let hub: Sentry.Hub
let sentry: Sentry.BrowserClient
let environment: string

/** Creates a valid sentry environment */
function getSentryEnvironment(): string {
  const hostname = location && location.hostname
  const result = kebabCase([hostname, getEnvironment()].filter(Boolean).join('-'))
  if (result !== environment) debug('environment: %s', result)
  return result
}

/** Utilizes the `shopifyStoreId` to select between `development` and `production` */
function getEnvironment(): 'production' | 'development' {
  const conf = getPrintButtonProps()
  const env = conf?.shopifyStoreId === 'infinite-objects-inc.myshopify.com' ? 'production' : 'development'
  return env
}

export function initSentry(): void {
  environment = getSentryEnvironment()
  sentry = new Sentry.BrowserClient({
    environment,
    dsn: SENTRY_DSN,
    release: `print-button@${VERSION}`,
    tracesSampleRate: getEnvironment() === 'development' ? 1.0 : 0.2,
    transport: Sentry.makeFetchTransport,
    stackParser: Sentry.defaultStackParser,
    integrations: [
      new Sentry.Breadcrumbs(),
      // new Sentry.LinkedErrors(),
      // new Sentry.Dedupe(),
    ],
  })
  debug('Initialized Sentry with env: %s', environment)
  hub = new Sentry.Hub()
  hub.bindClient(sentry)
}

interface SentryInstance {
  sentry: Sentry.BrowserClient
  hub: Sentry.Hub
}

export function getSentry(): SentryInstance {
  if (!(hub && sentry && getSentryEnvironment() === environment)) {
    initSentry()
  }

  return {
    sentry,
    hub,
  }
}

/**
 * Sends breadcrumbs to Sentry
 */
export function addBreadcrumb(crumb: Sentry.Breadcrumb): void {
  const { hub } = getSentry()
  hub.addBreadcrumb(crumb)
}
